import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import '../exercise-types/index.ts';
import { ApiError, api } from '../../lib/api/client.js';
import type {
  ExercisePrompt,
  LearnLesson,
  LearningSessionResponse,
} from '../../lib/api/types.js';
import {
  describeMiss,
  getRenderer,
  UnknownExerciseRendererError,
  type ExercisePhase,
  type ExerciseRenderer,
  type MissDescription,
  type SubmitResult,
} from '../exercise-types/registry.ts';
import { useCurrentUser } from '../../app/AuthProvider.tsx';
import { AllComplete } from './AllComplete.tsx';
import { BlockedExercise } from './BlockedExercise.tsx';
import { NoContent } from './NoContent.tsx';
import { SessionSummary } from './SessionSummary.tsx';
import { SubmissionFailure } from './SubmissionFailure.tsx';
import { TaskBar } from '../../ui/components/Navigation.tsx';
import { Dialog } from '../../ui/components/Dialog/Dialog.tsx';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useDelayedLoading } from '../../ui/components/LoadingState/useDelayedLoading.ts';
import { OfflineState } from '../../ui/components/OfflineState/OfflineState.tsx';
import { ProgressBar } from '../../ui/components/Progress/ProgressBar.tsx';
import { AmbientField } from '../../ui/primitives/AmbientField.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { DEFAULT_LOCALE } from '@cavaquinho/shared';
import styles from './practice.module.css';

/**
 * The type-agnostic practice shell (T056, T116).
 *
 * Everything in this file is about SESSIONS, ATTEMPTS, PHASES, TIMING and
 * FOCUS. It knows nothing about notes, audio, or answer options — that
 * knowledge belongs entirely to the Exercise Type's own renderer, looked up
 * through the registry (`../exercise-types/index.ts`, imported here for its
 * registration side effect and nowhere else). Adding a second Exercise Type
 * must cost a handler and a renderer, never a line in this file (SC-009).
 *
 * `src/ui`'s `Button` deliberately accepts no `className`, `style`, or
 * `data-testid` — it owns its own DOM, and the design system forbids a
 * feature reaching past that boundary (see `Button.tsx`'s own comment). Every
 * `data-testid` this task requires on a `Button` is therefore carried on a
 * small transparent wrapper around it instead of on the component itself.
 */

/**
 * `unavailable` is "we could not reach the platform"; `blocked` is "the
 * platform answered, and this Exercise cannot be opened". Collapsing the two
 * is what put a learner in front of "check your connection" for an Exercise
 * whose configuration the server had just refused — a message that is wrong
 * about the cause and, worse, offers a retry of the one thing guaranteed to
 * fail again.
 */
type BootStatus = 'loading' | 'ready' | 'unavailable' | 'blocked';

/**
 * A RETRY BELONGS TO THE SCREEN THAT OFFERED IT.
 *
 * Every retry on this shell used to go back through `loading`, and `loading`
 * is a whole-screen illustration of a cavaquinho lifting into place. A
 * refused Exercise answers in a few dozen milliseconds, so activating
 * "Tentar de novo" tore the screen down, flashed the instrument, and built
 * the same screen back — motion the learner reads as a glitch rather than as
 * an answer to what they pressed.
 *
 * So a retry no longer changes WHICH SCREEN IS ON. It reports itself in the
 * control that started it, leaves everything else exactly where the learner
 * left it, and swaps the screen only once there is a different screen to
 * show. `loading` stays what it always was — the first resolve, when there is
 * genuinely nothing on screen yet to keep.
 */
type BootMode = 'first' | 'retry';

/**
 * The floor a retry is held to. Below roughly this, a pending control comes
 * and goes inside one blink: the learner sees the screen twitch and cannot
 * tell whether their press was even taken. The wait is spent on the state
 * they are already reading, not on an empty screen, so it costs them nothing.
 */
const MIN_RETRY_MS = 450;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * What a bootstrap resolved to, captured whole rather than written to state
 * as it goes: a retry applies its outcome in one pass, at the moment the
 * screen is allowed to change, and state half-written before that would be a
 * half-updated screen under the learner's eyes.
 */
type BootOutcome =
  | {
      readonly kind: 'ready';
      readonly session: LearningSessionResponse;
      readonly lessons: readonly LearnLesson[];
      readonly attempt: ExercisePrompt | null;
    }
  | {
      readonly kind: 'blocked';
      readonly session: LearningSessionResponse;
      readonly lessons: readonly LearnLesson[];
      readonly lesson: { id: string; title: string };
    }
  | { readonly kind: 'unavailable' };

/**
 * Reads the session, the catalogue and the attempt the cursor stands on, and
 * says which of the three screens that adds up to. It touches no state of its
 * own, which is what lets the caller decide WHEN the answer lands.
 */
async function resolveBoot(): Promise<BootOutcome> {
  try {
    /*
       The catalogue read is DELIBERATELY NOT FATAL. It feeds the task bar's
       position, which is orientation; the session and the attempt are the
       exercise itself. A learner who can practise must not be shown a
       connection error because a counter could not be drawn, so this one
       failure degrades to a task bar with a back control and no progress.
    */
    const [session, content] = await Promise.all([
      api.startOrResumeSession(),
      api.lessons().catch(() => ({ lessons: [] })),
    ]);
    if (session.position) {
      try {
        const attempt = await api.presentExercise(session.position.exercise.id);
        return { kind: 'ready', session, lessons: content.lessons, attempt };
      } catch (error) {
        /*
          THE PLATFORM ANSWERED, AND THE ANSWER WAS NO.

          An `ApiError` means the request reached the server and came back
          with a status: a configuration the Exercise Type refuses (422), an
          Exercise deleted out from under the cursor (404), an Exercise Type
          this deployment does not carry (500). None of those is a connection
          problem, and none of them is fixed by trying again — which is the
          whole of what the offline state was offering.

          It is also not a dead end for the learner, only for this one
          Exercise, and the blocked state says so: the Lesson they are in is
          named, and both ways out of it are on screen. Anything that is NOT
          an `ApiError` — a dropped connection, a request that never
          resolved — falls through to the catch outside, which is where it
          belongs.
        */
        if (!(error instanceof ApiError)) throw error;
        return {
          kind: 'blocked',
          session,
          lessons: content.lessons,
          lesson: session.position.lesson,
        };
      }
    }
    return { kind: 'ready', session, lessons: content.lessons, attempt: null };
  } catch {
    // A network failure or a 5xx here is not FR-055's territory — nothing
    // has been submitted yet — but it still must not be silent. The floor
    // is a visible retry; anything richer is out of this task's scope.
    return { kind: 'unavailable' };
  }
}

/** One recorded attempt, as this shell watched it resolve. */
interface SittingResult {
  readonly exerciseType: string;
  readonly correct: boolean;
  readonly publicFeedback: unknown;
}

/**
 * WHAT HAPPENED SINCE THE LEARNER ARRIVED ON THIS SCREEN.
 *
 * A Learning Session never ends (FR-058) and the server records no per-run
 * result, so a "session" in the sense the learner means it — this sitting,
 * these eight exercises — exists nowhere but here. This shell is its only
 * witness: it submitted the attempts and was handed the outcomes, so it can
 * report them without asking anyone, and it reports nothing else.
 *
 * The sitting is scoped to ONE LESSON. Crossing into the next one ends the
 * run and starts a fresh sitting, because "how did that go" is a question
 * about the thing that just finished.
 */
interface Sitting {
  /** Attempts submitted and recorded here, in order. */
  readonly results: readonly SittingResult[];
  /**
   * Exercises SOLVED here — answered correctly and advanced past, which is
   * what marks one complete (FR-057).
   *
   * Not the number of advances. A missed Exercise advances the learner like
   * any other and comes back later in the same Lesson (FR-082), so counting
   * advances would report progress the learner has not made and would count
   * one Exercise twice when they get it on the second pass.
   */
  readonly solved: number;
}

/** A finished run, captured whole before the cursor moves on. */
interface EndedSitting extends Sitting {
  readonly lesson: { id: string; title: string } | null;
}

const EMPTY_SITTING: Sitting = { results: [], solved: 0 };

export function PracticeShell() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<BootStatus>('loading');
  const [session, setSession] = useState<LearningSessionResponse | null>(null);
  /** Where the learner was standing when the Exercise refused to open. */
  const [blocked, setBlocked] = useState<{ lesson: { id: string; title: string } } | null>(null);
  const [attempt, setAttempt] = useState<ExercisePrompt | null>(null);
  /*
   * The Lesson structure behind the cursor, read once so § 14's task bar can
   * show WHERE IN THE LESSON the learner is.
   *
   * The session alone cannot say: it names the current Exercise, not how many
   * the Lesson holds or which of them this is. `GET /learn/lessons` carries
   * both, and array order IS the ordering contract (lib/api/types.ts), so the
   * index of the current Exercise in `lesson.exercises` is the position — no
   * new field, no new endpoint, and no arithmetic the server has not already
   * fixed.
   *
   * Read ONCE at bootstrap and not again on every advance: what changes as
   * the learner works is the cursor, which the session already reports, and
   * re-fetching the whole catalogue between two Exercises would spend a round
   * trip to be told the same list. A Lesson an administrator extends mid-
   * practice is the one case this reads stale, and it self-corrects on the
   * next visit — the same freshness the journey screen has.
   */
  const [lessons, setLessons] = useState<readonly LearnLesson[]>([]);
  const [phase, setPhase] = useState<ExercisePhase>('answering');
  const [feedback, setFeedback] = useState<SubmitResult | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [canAdvance, setCanAdvance] = useState(false);
  const [nextInFlight, setNextInFlight] = useState(false);
  const [left, setLeft] = useState(false);
  const [sitting, setSitting] = useState<Sitting>(EMPTY_SITTING);
  const [ended, setEnded] = useState<EndedSitting | null>(null);
  const locale = useCurrentUser()?.locale ?? DEFAULT_LOCALE;

  // The response last handed to `submit`, kept only so `submit-retry` can
  // resend it without asking the renderer to re-collect an answer the
  // learner already gave once (FR-055's "make the failure visible", not
  // "make the learner answer twice").
  const lastResponseRef = useRef<unknown>(null);

  const regionRef = useRef<HTMLElement | null>(null);
  // Whether an attempt has ever resolved before this one. Focus moves into
  // the exercise region on every advance EXCEPT the very first — the learner
  // has not activated anything yet at first mount (FR-063).
  const hasShownAnAttempt = useRef(false);

  /**
   * Whether a retry is in flight. The screen it was started from stays on
   * exactly as it is; this is what lets the control that started it say so,
   * which is the whole of the feedback a retry now gives.
   */
  const [retrying, setRetrying] = useState(false);
  const retryingRef = useRef(false);

  // Most boots land inside the delay and go straight to the Exercise; one that
  // does not keeps the loader up long enough to be read as one.
  const showLoading = useDelayedLoading(status === 'loading');

  const bootstrap = useCallback(async (mode: BootMode = 'first') => {
    if (mode === 'retry') {
      // A second press while the first is still out would resolve twice and
      // apply twice; the control is disabled meanwhile, and this covers the
      // keyboard repeat that gets past it.
      if (retryingRef.current) return;
      retryingRef.current = true;
      setRetrying(true);
    } else {
      setStatus('loading');
    }

    const floor = mode === 'retry' ? wait(MIN_RETRY_MS) : Promise.resolve();
    const outcome = await resolveBoot();
    await floor;

    /*
      EVERY RESET LIVES HERE, WITH THE OUTCOME IT BELONGS TO.

      They used to run the moment a bootstrap started, which was harmless when
      the screen was about to be replaced by the loading one and is not now: a
      retry from the blocked state would clear the Lesson out of the state
      still on screen, and the learner would watch the Lesson's name vanish
      from a screen that was otherwise standing still.
    */
    setLeft(false);
    setSitting(EMPTY_SITTING);
    setEnded(null);
    setSubmitFailed(false);

    if (outcome.kind === 'unavailable') {
      setStatus('unavailable');
    } else if (outcome.kind === 'blocked') {
      setSession(outcome.session);
      setLessons(outcome.lessons);
      setBlocked({ lesson: outcome.lesson });
      setStatus('blocked');
    } else {
      setSession(outcome.session);
      setLessons(outcome.lessons);
      setAttempt(outcome.attempt);
      setBlocked(null);
      /*
        AN ATTEMPT THAT COMES BACK ALREADY ANSWERED OPENS ON ITS RESULT.

        The learner answered, then left before activating "Próxima" — so the
        completion was never written and their position is still this
        Exercise. The server hands the attempt back with the judgement it was
        given (attempts.service.ts), and the shell restores the phase that
        judgement belongs to. Anything else re-asks a question they have
        already answered, or leaves them on a position with no way forward.
      */
      setFeedback(outcome.attempt?.feedback ?? null);
      setPhase(outcome.attempt?.feedback ? 'feedback' : 'answering');
      setStatus('ready');
    }

    retryingRef.current = false;
    setRetrying(false);
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  /**
   * Submits a response for the current attempt. Passed to the renderer as
   * `onSubmit` and reused directly by the `submit-retry` action below — both
   * are the same durability-or-visible-failure contract (FR-055).
   */
  const submit = useCallback(
    async (response: unknown): Promise<SubmitResult> => {
      if (!attempt) throw new Error('submit called with no active attempt');
      lastResponseRef.current = response;
      setSubmitFailed(false);
      setPhase('submitting');
      try {
        const result = await api.submitAttempt({
          exerciseId: attempt.exerciseId,
          shown: attempt.publicPrompt,
          response,
        });
        setFeedback(result);
        setPhase('feedback');
        /*
          The attempt is durably recorded by the time this line runs (FR-055),
          so the sitting can count it. It is appended AFTER the await and
          never before: a submission that failed is not an attempt, and the
          summary must never report one the ledger does not hold.
        */
        setSitting((current) => ({
          ...current,
          results: [
            ...current.results,
            {
              exerciseType: attempt.exerciseType,
              correct: result.correct,
              publicFeedback: result.publicFeedback,
            },
          ],
        }));
        return result;
      } catch (error) {
        // FR-055 — never report this attempt as recorded. Feedback is never
        // rendered, and phase returns to 'answering' so the renderer's own
        // submission lock releases and a retry (from here, or from a fresh
        // selection) is possible. `SubmissionFailure` is what the learner
        // then sees, and it resends this same response rather than asking
        // for another (T074).
        setSubmitFailed(true);
        setPhase('answering');
        throw error;
      }
    },
    [attempt],
  );

  const retrySubmit = useCallback(() => {
    if (lastResponseRef.current === null) return;
    void submit(lastResponseRef.current).catch(() => {
      // Already surfaced via `submitFailed`; nothing further to do here.
    });
  }, [submit]);

  /*
   * FR-023 — the 1.5 s floor is measured from the moment feedback BECOMES
   * VISIBLE, not from submission. `useEffect` is the correct anchor for that:
   * it runs after React has committed the DOM the browser paints, which is
   * the first moment the feedback could actually be on screen. The duration
   * itself, `feedback.feedbackMinimumMs`, comes from the server response and
   * is never hardcoded — a future Exercise Type that serves a different
   * value, including 0, needs no change here (FR-025).
   */
  useEffect(() => {
    if (!feedback) {
      setCanAdvance(false);
      return;
    }
    setCanAdvance(false);
    const timer = setTimeout(() => setCanAdvance(true), feedback.feedbackMinimumMs);
    return () => clearTimeout(timer);
  }, [feedback]);

  /**
   * Advances past the current Exercise (FR-024, FR-054). Feedback stays on
   * screen for the whole of this async work — `session` and `attempt` are
   * swapped together, atomically, only once the next attempt has actually
   * resolved. That "hold the old view, fetch the new one" sequencing IS
   * research D-010's prefetch: the next sample's URL cannot be known before
   * `POST /learn/next` completes, so this fetch, made during the feedback
   * window the learner is already spending reading the result, is what fills
   * it. `nextInFlight` guards a second activation while it runs.
   */
  const onNext = useCallback(() => {
    if (nextInFlight || !canAdvance) return;
    // Read before the advance: once the cursor has moved, the Lesson the
    // learner was IN is no longer anywhere on the response.
    const leaving = session?.position?.lesson ?? null;
    // Which Exercise is being left, for the same reason: the wrap of FR-082 is
    // detected by comparing where the cursor lands against where it started,
    // and once it has moved the starting point is gone.
    const leavingExerciseId = session?.position?.exercise.id ?? null;
    // Read here for the same reason: this is the judgement of the Exercise
    // being advanced past, and the next one's replaces it below.
    const solvedThisOne = feedback?.correct === true;
    setNextInFlight(true);
    void (async () => {
      /*
        TWO REQUESTS, AND ONLY THE FIRST IS SAFE TO ABANDON.

        These used to share one `try`, under a comment arguing that a failed
        advance recorded nothing because the completion write and the cursor
        move are one transaction on the server (next.controller.ts). That is
        true of `POST /learn/next` and FALSE of everything after it: by the
        time the prompt is fetched the advance HAS happened. So a prefetch
        that failed rolled the whole block back on the client while the server
        held the opposite view — the shell kept showing the finished Exercise,
        the summary never opened, and pressing "Próxima" again asked the
        server to advance past an Exercise the cursor had already left, which
        is a 409 the same handler swallowed. The button was dead from then on,
        which is exactly the dead end FR-054 exists to prevent at the end of a
        Lesson.

        So the two are separated by what they cost to lose. The advance may be
        retried; ONCE IT RETURNS, EVERY LINE BELOW RUNS.
      */
      let nextSession: LearningSessionResponse;
      try {
        nextSession = await api.next();
      } catch {
        // Nothing was recorded — one transaction, so it happened wholly or
        // not at all. The current Exercise's feedback and attempt are
        // untouched, so "Próxima" simply becomes available again.
        setNextInFlight(false);
        return;
      }

      /*
        Committed. The prompt ahead is a convenience — the position is already
        the server's truth — so a failure here costs this fetch and NOT the
        advance. `blocked` is where bootstrap already sends an Exercise the
        platform refuses to open, and it carries both ways out; freezing in
        place carries none.
      */
      let nextAttempt: ExercisePrompt | null = null;
      let couldNotOpen = false;
      if (nextSession.position) {
        try {
          nextAttempt = await api.presentExercise(nextSession.position.exercise.id);
        } catch {
          couldNotOpen = true;
        }
      }

      setSession(nextSession);
      setAttempt(nextAttempt);
      // The same resume rule as bootstrap: the Exercise advanced to can
      // already hold an answer given on another device.
      setFeedback(nextAttempt?.feedback ?? null);
      setSubmitFailed(false);
      setPhase(nextAttempt?.feedback ? 'feedback' : 'answering');

      /*
        THE END OF A PASS IS A BOUNDARY THE SERVER DREW, AND THERE ARE TWO.

        `POST /learn/next` moves the cursor and records the outcome in one
        transaction, so where the cursor LANDS is the platform saying what just
        happened. Two landings end the run:

         - a different Lesson, or nowhere at all (the content-complete state):
           this Lesson is finished, every Exercise in it answered correctly;
         - the same Lesson, at or before the Exercise just left: the wrap of
           FR-082. The Lesson ran out of unanswered Exercises and sent the
           learner back to the first one still outstanding.

        The second used to fall through and simply put the learner on Exercise
        one again, which is indistinguishable from the app being stuck — answer
        the last question of a Lesson, land on its first, nothing said. Both
        are the end of a pass, so both end on the same screen.

        Sequence comes from the catalogue read at bootstrap, which is all this
        needs: an administrator can add or remove Exercises, but the ORDER of
        the ones on screen cannot change under a practising learner, and the
        review being navigated to reads its own state fresh anyway.
      */
      const solved = sitting.solved + (solvedThisOne ? 1 : 0);
      const arriving = nextSession.position?.lesson ?? null;

      const shape = lessons.find((entry) => entry.id === leaving?.id);
      const leftAt = shape?.exercises.findIndex((entry) => entry.id === leavingExerciseId) ?? -1;
      const arrivedAt =
        shape?.exercises.findIndex((entry) => entry.id === nextSession.position?.exercise.id) ?? -1;
      const wrappedBack = leftAt >= 0 && arrivedAt >= 0 && arrivedAt <= leftAt;

      const endsRun = leaving !== null && (arriving?.id !== leaving.id || wrappedBack);

      /*
        AND A FINISHED LESSON IS REVIEWED, NOT SUMMARISED.

        `/revisao` is the screen that answers "how did that go" (FR-054's
        completed state, features/content/LessonReview.tsx): the Lesson's own
        title, what the learner recognised, what escaped, and the way back
        into it — all read from the recorded attempts rather than counted by
        this browser. Finishing the Lesson is precisely when that question is
        being asked, so the loop hands it the Lesson it just left, exactly as
        the spine does from a finished node.

        `SessionSummary` keeps the one job it can do that the server cannot:
        reporting a sitting the learner ENDED EARLY, from `onExit` below,
        where there is no finished Lesson to review.
      */
      if (endsRun) {
        setSitting(EMPTY_SITTING);
        setNextInFlight(false);
        navigate('/revisao', { state: { lessonId: leaving.id, from: 'practice' } });
        return;
      }

      setSitting({ ...sitting, solved });

      /*
        Still inside the Lesson, and the Exercise ahead would not open. That
        is bootstrap's `blocked` state — it names where the learner is and
        carries both ways out — and it is the alternative to freezing on a
        "Próxima" that has already been spent.
      */
      if (couldNotOpen) {
        setBlocked({ lesson: arriving ?? { id: '', title: '' } });
        setStatus('blocked');
      }

      setNextInFlight(false);
    })();
  }, [nextInFlight, canAdvance, session, sitting, feedback, lessons, navigate]);

  // FR-063 — focus the new Exercise's interactive region on every advance,
  // never on the very first attempt of a visit, and never to `document.body`.
  useEffect(() => {
    if (!attempt) return;

    /*
     * The region is the screen's scroll container (practice.module.css), and
     * the element itself survives an advance — only the renderer inside it is
     * re-keyed. So a learner who scrolled a seven-option grid on the Exercise
     * that just ended would land part-way down the next one, on a scroll
     * position that belongs to content no longer on screen. Reset before the
     * focus move, and on the first attempt too: there is no advance to skip
     * here, only a position to be sure of.
     */
    if (regionRef.current) regionRef.current.scrollTop = 0;

    if (!hasShownAnAttempt.current) {
      hasShownAnAttempt.current = true;
      return;
    }
    regionRef.current?.focus();
  }, [attempt?.promptId]);

  /*
   * A RESOLVED EXERCISE IS READ FROM THE BOTTOM OF ITS COLUMN.
   *
   * While the learner is answering, the orb is what the screen is about and
   * the top of the region is where it belongs (§ 1.5). The moment an answer
   * resolves that inverts: what matters is the tile that was theirs, the tile
   * that was right and the panel naming both (FR-069) — and all three are at
   * the FOOT of the column, under an orb and a question that have done their
   * job.
   *
   * On the sizes where the Exercise is taller than the frame the region
   * gives it (a short laptop window, a phone), leaving the scroller at the
   * top puts the option grid behind the docked result and shows the learner a
   * verdict with nothing to attach it to. The end of the column is the
   * reading position a resolved Exercise actually has, so the screen takes
   * it rather than asking for a scroll.
   *
   * It is a no-op wherever everything already fits, which is every size with
   * the height for it — there is nothing to scroll. And it stays type-
   * agnostic: "the result is the last thing in the region" is a fact about
   * this shell's own composition, not about notes or options (SC-009).
   */
  useEffect(() => {
    if (phase !== 'feedback') return;
    const region = regionRef.current;
    if (!region) return;
    region.scrollTop = region.scrollHeight;
  }, [phase, attempt?.promptId]);

  /*
   * T116 — leaving calls no endpoint. A pending attempt has no
   * `submitted_at`, so nothing was recorded and nothing needs undoing; the
   * session cursor is untouched, so the learner resumes on the same Exercise
   * (FR-070). The only path this shell exposes toward `exercise_completion`
   * is a submitted attempt followed by `POST /learn/next` — there is no
   * skip, structurally, not by a check the learner could route around
   * (FR-070, SC-018).
   *
   * Exiting ASKS rather than leaves: it keeps the position claim — "you
   * resume exactly here" — directly observable at the moment the learner
   * steps away, which is when they would otherwise wonder, and it offers both
   * ways out so it is a fork rather than the dead end it was before the
   * journey screen existed. The asking is a dialog over the Exercise
   * (`Dialog`, below), so the thing being stepped away from is still on
   * screen while the learner decides.
   */
  const onExit = useCallback(() => {
    // A sitting with something in it ends on its summary; an empty one has
    // nothing to report, so it gets the plain "your place is kept" question.
    if (sitting.results.length > 0) {
      setEnded({ lesson: session?.position?.lesson ?? null, ...sitting });
      return;
    }
    setLeft(true);
  }, [sitting, session]);

  /*
   * Dismissing the question, and nothing more.
   *
   * It used to re-run `bootstrap`, because the paused state had replaced the
   * whole screen and there was nothing left to return to. A dialog tears
   * nothing down: the session, the attempt, the phase and the sitting are all
   * exactly as the learner left them a moment ago, so resuming is closing the
   * dialog — no round trip, and no chance of the reload landing them somewhere
   * other than where the dialog just promised they would stay.
   */
  const onResume = useCallback(() => setLeft(false), []);

  /**
   * Back into the loop from the summary.
   *
   * Nothing is torn down or re-fetched: the shell's session and attempt are
   * exactly where they were, whether the summary arrived at a Lesson boundary
   * (the cursor already advanced) or on the way out (it never moved). Only
   * the sitting starts over, because the run being counted is a new one.
   */
  const onContinueFromSummary = useCallback(() => {
    setEnded(null);
    setSitting(EMPTY_SITTING);
  }, []);

  /*
    THE WAY OUT IS ON EVERY STATE OF THIS SCREEN, NOT ONLY THE WORKING ONE.

    Practice sits outside `AppLayout` (router.tsx) — § 14 gives a focused task
    a task bar instead of the navigation pill — so the task bar's back control
    is the ONLY route off this route. While it was rendered for the ready
    state alone, the two states a learner is least able to act on, waiting and
    failing, were the two with nothing to press: a boot that could not resolve
    left them on an error panel whose single button retried the thing that had
    just failed, and the browser's own back button was the only escape.

    Here it goes to the journey rather than to `onExit`'s paused state: there
    is no sitting to close and no position to promise to keep, because neither
    has loaded.
  */
  if (status === 'loading' || showLoading) {
    return (
      <Screen>
        <header className={styles.header}>
          <div data-testid="exit">
            <TaskBar backLabel="Voltar às lições" onBack={() => navigate('/')} />
          </div>
        </header>
        {showLoading ? <LoadingState label="Carregando sua prática" /> : null}
      </Screen>
    );
  }

  if (status === 'unavailable') {
    return (
      <Screen>
        <header className={styles.header}>
          <div data-testid="exit">
            <TaskBar backLabel="Voltar às lições" onBack={() => navigate('/')} />
          </div>
        </header>
        <OfflineState
          title="Não foi possível carregar sua prática"
          onRetry={() => void bootstrap('retry')}
          retrying={retrying}
        />
      </Screen>
    );
  }

  /*
    One Exercise cannot be opened — and the learner is standing on it, because
    the cursor is exactly where the platform put them. Without somewhere else
    to go this is the end of the product for them: the position does not move
    until an Exercise is answered, and this one cannot be answered.

    So the state names the Lesson and leaves the way back to the journey on
    the task bar, where it is on every other state of this screen.
  */
  if (status === 'blocked') {
    return (
      <Screen>
        <header className={styles.header}>
          <div data-testid="exit">
            <TaskBar backLabel="Voltar às lições" onBack={() => navigate('/')} />
          </div>
        </header>
        <BlockedExercise
          lessonTitle={blocked?.lesson.title ?? null}
          onRetry={() => void bootstrap('retry')}
          retrying={retrying}
        />
      </Screen>
    );
  }

  /*
    THE PASS BOUNDARY, BEFORE THE SITTING SCREENS.

    It takes the whole screen for the same reason they do: it exists to be
    decided, and the Exercise behind it is one the learner has already seen.
    Continuing simply dismisses it — the session and the attempt underneath
    are the ones `onNext` already fetched, so there is nothing to re-read and
    the next Exercise is on screen the moment the button is pressed.
  */

  /*
    A sitting the learner ENDED EARLY, on grad/success — § 4's "one moment the
    system raises intensity", spent on the one moment worth it. It takes the
    whole screen because § 13 gives a completion state a result and ONE
    decision, and a task bar offering the way out beside a result that has
    already ended the run would be a second one.

    A Lesson carried to its end leaves by `onNext` instead, for `/revisao`:
    there the server holds the record, so the screen can report what was
    practised rather than what this browser happened to witness. This one is
    what remains when there is no finished Lesson to read back — the learner
    stopped in the middle, and the run still deserves an account of itself.
  */
  if (ended) {
    const lesson = ended.lesson
      ? (lessons.find((candidate) => candidate.id === ended.lesson?.id) ?? null)
      : null;
    const total = lesson?.exercises.length ?? 0;
    const baseline = lesson?.exercises.filter((item) => item.completed).length ?? 0;

    const misses = ended.results
      .filter((result) => !result.correct)
      .map((result) => describeMiss(result.exerciseType, result.publicFeedback, locale))
      .filter((description): description is MissDescription => description !== null);

    return (
      <Screen celebrating>
        <SessionSummary
          lessonTitle={ended.lesson?.title ?? null}
          recorded={ended.results.length}
          correct={ended.results.filter((result) => result.correct).length}
          /*
            The catalogue was read at bootstrap, so its completion counts are
            from before this sitting; the exercises SOLVED since are added to
            them. Solved rather than seen: a missed Exercise is still ahead of
            the learner, and counting it here would show them progress the
            Lesson has not recorded. `min` is the honest ceiling — a Lesson
            cannot be more than finished, and an administrator who shortened it
            mid-run must not produce "09 of 8".
          */
          lessonProgress={
            total > 0 ? { done: Math.min(baseline + ended.solved, total), total } : null
          }
          misses={misses}
          onContinue={session?.position ? onContinueFromSummary : undefined}
          onFinish={() => navigate('/')}
        />
      </Screen>
    );
  }

  // `status === 'ready'` from here on; `session` is always set by then.
  const currentSession = session as LearningSessionResponse;
  const position = currentSession.position;

  /*
   * § 14 Progress — one segment per Exercise in the Lesson, so the structure
   * of the sequence is legible in the bar itself, and the frontier is where
   * the learner actually is.
   *
   * Both numbers come from the catalogue read at bootstrap; array order IS
   * the ordering contract, so the index of the current Exercise is the
   * position. When the catalogue is missing, or the cursor points at content
   * this read does not contain, the task bar simply carries no progress
   * rather than a bar built on a guess.
   */
  const currentLesson = position
    ? (lessons.find((candidate) => candidate.id === position.lesson.id) ?? null)
    : null;
  const exerciseIndex =
    currentLesson && position
      ? currentLesson.exercises.findIndex((candidate) => candidate.id === position.exercise.id)
      : -1;

  /*
   * A RESULT DOES NOT CHANGE WHAT THIS SCREEN IS.
   *
   * The ground, the atmosphere and the task bar are the same for a hit as for
   * a miss, because both are the same screen reporting an answer — swapping
   * the ground to grad/success and taking the escape away on a correct answer
   * moved the learner somewhere else at the one moment they were sure of where
   * they were. § 4's rise in intensity is spent where the result is: the
   * feedback panel, which the renderer owns.
   *
   * The whole-screen gradient is still the session summary's (`<Screen
   * celebrating>` above) — that is a moment with nothing behind it to preserve.
   */

  return (
    <Screen>
      {/*
        § 14 Navigation — the task bar: BACK + PROGRESS + POSITION, and no
        other control, so it does not compete with the screen's dominant
        object. The escape lives here rather than beside "Próxima" for exactly
        that reason: a footer holding both gave the way out and the way on the
        same weight, which is the opposite of § 1.6's one decision per screen.

        `data-testid` sits on a transparent wrapper because `src/ui`
        components own their own DOM and accept neither `className` nor a test
        hook (see `Button.tsx`). The bar holds exactly one button, so the hook
        still resolves to the escape and to nothing else.
      */}
      <header className={styles.header}>
        <div data-testid="exit">
          <TaskBar
            backLabel="Sair da lição"
            onBack={onExit}
            progress={
              currentLesson && exerciseIndex >= 0 ? (
                <ProgressBar
                  total={currentLesson.exercises.length}
                  current={exerciseIndex}
                  label={`Progresso em ${currentLesson.title}`}
                />
              ) : undefined
            }
          />
        </div>

        {/*
          The Lesson's name, quietly. § 14 keeps the task bar to three things,
          and § 1.5 gives this screen's loudest voice to the sound — so the
          title orients without competing, and the one display step § 5.2
          allows here belongs to the Exercise's own question.
        */}
        {position ? (
          <Text as="p" variant="body-sm" tone="muted">
            {position.lesson.title}
          </Text>
        ) : null}
      </header>

      {/*
        LEAVING IS A QUESTION ASKED OVER THE EXERCISE, NOT A SCREEN OF ITS OWN.

        This was a full-screen "Até já" state, which spent a whole screen on
        one sentence and took the Exercise away to say it — so the learner
        read a promise about a position they could no longer see, and getting
        back cost a full re-bootstrap of a shell that had never been torn
        down. Here the exercise stays behind the scrim, "Continuar" simply
        closes, and the sentence is doing the one job it ever had: answering
        "will I lose my place?" at the moment the question is asked.
      */}
      <Dialog
        open={left}
        title="Sair da lição?"
        onClose={onResume}
        testId="left-lesson"
        actions={
          <>
            <Button variant="ghost" icon="arrow-left" onClick={() => navigate('/')}>
              Ver as lições
            </Button>
            <span data-testid="resume">
              <Button variant="primary-gradient" icon="play" onClick={onResume}>
                Continuar praticando
              </Button>
            </span>
          </>
        }
      >
        <Text as="p" variant="body">
          Sua posição foi guardada. Você volta exatamente para este exercício.
        </Text>
      </Dialog>

      {position && attempt ? (
        <ExerciseRegion
          regionRef={regionRef}
          attempt={attempt}
          phase={phase}
          feedback={feedback}
          submitFailed={submitFailed}
          onSubmit={submit}
          onNext={onNext}
          onRetry={retrySubmit}
        />
      ) : currentSession.allLessonsComplete ? (
        <AllComplete />
      ) : (
        <NoContent onRetry={() => void bootstrap('retry')} retrying={retrying} />
      )}

      {/*
        One action, and it is on screen only once there is something to
        advance to (§ 1.6). While the learner is answering, the footer is
        empty and the orb is uncontested.
      */}
      {phase === 'feedback' ? (
        <footer className={styles.footer}>
          <span data-testid="next">
            {canAdvance && !nextInFlight ? (
              <Button id="next" onClick={onNext}>
                Próxima
              </Button>
            ) : (
              <Button
                id="next"
                disabled
                disabledReason={
                  canAdvance ? 'Carregando o próximo exercício.' : 'Aguarde o fim do retorno.'
                }
              >
                Próxima
              </Button>
            )}
          </span>
        </footer>
      ) : null}
    </Screen>
  );
}

/**
 * The practice screen's ground — § 8's L0, which is defined as `background`
 * PLUS `grad/ambient`.
 *
 * The field is a SIBLING of the column rather than a child of it, so the
 * blobs hang off the edges of the viewport while the task itself stays inside
 * § 7's 560px focus column. Two of the three § 4 allows: this screen already
 * spends its one loud gesture on the orb (§ 1.10), and the atmosphere is here
 * to give that circle somewhere to sit, not to compete with it.
 */
function Screen({
  children,
  celebrating = false,
}: {
  children: ReactNode;
  celebrating?: boolean;
}) {
  return (
    <div className={styles.screen}>
      {/*
        L0 is one thing or the other, never both. Ambient blobs UNDER a
        celebration gradient would be a second decorative gesture nobody can
        see, and § 1.10 makes one per screen the rule rather than the budget.
      */}
      {celebrating ? (
        <span className={styles.celebration} aria-hidden="true" />
      ) : (
        <AmbientField count={2} anchor="viewport" />
      )}
      <div className={styles.page}>{children}</div>
    </div>
  );
}

/**
 * The exercise region: the renderer lookup, the FR-055 submission-failure
 * surface, and the focusable landing target for FR-063 — split out only so
 * `PracticeShell`'s own render body stays about session/phase state, not
 * about a single Exercise's markup.
 */
function ExerciseRegion({
  regionRef,
  attempt,
  phase,
  feedback,
  submitFailed,
  onSubmit,
  onNext,
  onRetry,
}: {
  regionRef: RefObject<HTMLElement | null>;
  attempt: ExercisePrompt;
  phase: ExercisePhase;
  feedback: SubmitResult | null;
  submitFailed: boolean;
  onSubmit: (response: unknown) => Promise<SubmitResult>;
  onNext: () => void;
  onRetry: () => void;
}) {
  let Renderer: ExerciseRenderer;
  try {
    Renderer = getRenderer(attempt.exerciseType);
  } catch (error) {
    if (error instanceof UnknownExerciseRendererError) {
      return (
        <section
          ref={regionRef}
          tabIndex={-1}
          data-testid="unknown-type"
          aria-label="Exercício"
          className={styles.region}
        >
          <Text as="h2" variant="heading">
            Não é possível mostrar este exercício
          </Text>
          <Text as="p" variant="body-sm" tone="muted">
            Nenhum tipo de exercício compatível foi encontrado para "{attempt.exerciseType}".
          </Text>
        </section>
      );
    }
    throw error;
  }

  return (
    <section
      ref={regionRef}
      tabIndex={-1}
      data-testid="exercise-region"
      aria-label="Exercício"
      className={styles.region}
    >
      {submitFailed ? (
        <SubmissionFailure onRetry={onRetry} retrying={phase === 'submitting'} />
      ) : null}

      {/*
        `key` — a new attempt gets a NEW renderer instance rather than a
        re-render of the previous one. Without it React reconciles by
        position and the same instance survives every advance, carrying its
        state with it: the submission lock, the current selection, and
        `useAudio`'s "has been heard" flag all belong to the Exercise that
        just ended, not to this one.
      */}
      <Renderer
        key={attempt.promptId}
        promptId={attempt.promptId}
        publicPrompt={attempt.publicPrompt}
        onSubmit={onSubmit}
        onNext={onNext}
        phase={phase}
        feedback={feedback ? feedback.publicFeedback : null}
      />
    </section>
  );
}
